<script setup lang="ts">
	import { useAwsConfiguration } from "@shared/configuration/aws-configuration";
	import { getEnvironmentSetting } from "@/composables/settings";
	import { S3Client } from "@aws-sdk/client-s3";
	import { getUserFromIdToken, type Group } from "@ptx-autonomous/libs/api";
	import {
		createPresignedDownloadUrl,
		type AccessibleObject,
	} from "@ptx-autonomous/libs/s3";
	import ListFiles from "@shared/components/Files/ListFiles.vue";
	import { useHelpFiles } from "@shared/composables/help-files";
	import { fetchAuthSession } from "aws-amplify/auth";
	import { computed, ref, watchEffect } from "vue";
	import { useI18n } from "vue-i18n";
	import VideoPlayer, {
		type PlaylistItem,
	} from "@shared/components/Files/VideoPlayer.vue";
	import { AsyncDialog } from "@ptx-autonomous/libs/confirm";
	const { t } = useI18n();
	import {
		getQualityVariantsFromList,
		groupVideosForDisplay,
	} from "@shared/utils/video-quality-keys";
	import type { VideoUrl } from "@shared/composables/help-files";
	import { buildPlaylistList } from "@shared/utils/video-playlist";

	const awsConfig = useAwsConfiguration(getEnvironmentSetting);

	const s3ResourceDocumentBucket = computed(
		() => awsConfig.value.s3Buckets.resourceDocument,
	);
	const region = computed(() => awsConfig.value.region);

	const isAdding = ref(false);
	const isEditing = ref(false);

	const downloadErrorMessage = ref<string | undefined>(undefined);
	const isPlayingVideo = ref(false);
	const selectedVideoUrl = ref<VideoUrl | null>(null);
	const selectedVideoTitle = ref<string | null>(null);
	const selectedVideoKey = ref<string | null>(null);
	const playlistItems = ref<PlaylistItem[]>([]);
	const videoPlayerDialog = ref<InstanceType<typeof AsyncDialog> | null>(
		null,
	);
	const availableGroups = ref<Group[]>([]); //User groups for the current user

	watchEffect(async () => {
		try {
			const user = await getUserFromIdToken();
			if (!user?.groups) {
				availableGroups.value = [];
				return;
			}

			availableGroups.value = user.groups as Group[];
		} catch (e) {
			console.log(e);
			availableGroups.value = [];
		}
	});

	const { files, isLoading, errorMessage } = useHelpFiles(
		availableGroups,
		region,
		s3ResourceDocumentBucket,
	);

	const displayFiles = computed(() =>
		groupVideosForDisplay(files.value ?? []),
	);

	function handleVideoPlayerClose(close: (result?: boolean) => void) {
		closeVideoPlayer();
		close();
	}
	const message = computed(() => {
		return errorMessage.value || downloadErrorMessage.value;
	});

	async function downloadHelpFiles(s3key: string) {
		downloadErrorMessage.value = "";

		try {
			const authSession = await fetchAuthSession();

			if (
				authSession.credentials?.accessKeyId === undefined ||
				authSession.credentials?.secretAccessKey === undefined ||
				authSession.credentials?.sessionToken === undefined
			) {
				downloadErrorMessage.value =
					"Please, login to download the certificate.";
				return false;
			}

			const s3Client = new S3Client({
				region: region.value,
				credentials: {
					accessKeyId: authSession.credentials.accessKeyId,
					secretAccessKey: authSession.credentials.secretAccessKey,
					sessionToken: authSession.credentials.sessionToken,
				},
			});
			const downloadUrl = await createPresignedDownloadUrl(
				s3Client,
				s3ResourceDocumentBucket.value,
				s3key,
			);
			window.open(downloadUrl, "_blank");
		} catch (error) {
			console.error(error);
			downloadErrorMessage.value = t("common.errorGettingLink", {
				key: s3key,
			});
		}
	}

	async function playVideoDocument(s3key: string) {
		downloadErrorMessage.value = undefined;
		const fileList = files.value ?? [];
		const allKeys = fileList.map((f: AccessibleObject) => f.key);
		const currentFile = fileList.find(
			(f: AccessibleObject) => f.key === s3key,
		);
		if (!currentFile) {
			downloadErrorMessage.value = t("common.videoNotFoundKey", {
				key: s3key,
			});
			return;
		}

		selectedVideoTitle.value =
			currentFile.allTags?.description || t("common.video");
		selectedVideoKey.value = s3key;
		selectedVideoUrl.value = "";
		isPlayingVideo.value = true;
		try {
			const authSession = await fetchAuthSession();

			if (
				authSession.credentials?.accessKeyId === undefined ||
				authSession.credentials?.secretAccessKey === undefined ||
				authSession.credentials?.sessionToken === undefined
			) {
				downloadErrorMessage.value = t("common.loginToPlayVideo");
				return false;
			}

			const s3Client = new S3Client({
				region: region.value,
				credentials: {
					accessKeyId: authSession.credentials.accessKeyId,
					secretAccessKey: authSession.credentials.secretAccessKey,
					sessionToken: authSession.credentials.sessionToken,
				},
			});

			// Build playlist items if this video belongs to a playlist
			playlistItems.value = await buildPlaylistList(
				currentFile,
				fileList,
				s3Client,
				s3ResourceDocumentBucket.value,
			);

			const variantMap = getQualityVariantsFromList(s3key, allKeys);
			const variantEntries = Object.entries(variantMap);

			if (variantEntries.length === 0) {
				if (!allKeys.includes(s3key)) {
					downloadErrorMessage.value = t("common.videoNotFoundKey", {
						key: s3key,
					});
					return false;
				}
				const downloadUrl = await createPresignedDownloadUrl(
					s3Client,
					s3ResourceDocumentBucket.value,
					s3key,
				);
				selectedVideoUrl.value = downloadUrl;
			} else if (variantEntries.length === 1) {
				const downloadUrl = await createPresignedDownloadUrl(
					s3Client,
					s3ResourceDocumentBucket.value,
					variantEntries[0][1],
				);
				selectedVideoUrl.value = downloadUrl;
			} else {
				const urls = await Promise.all(
					variantEntries.map(([, k]) =>
						createPresignedDownloadUrl(
							s3Client,
							s3ResourceDocumentBucket.value,
							k,
						),
					),
				);
				const urlByLabel: Record<string, string> = {};
				variantEntries.forEach(([label], i) => {
					urlByLabel[label] = urls[i];
				});
				selectedVideoUrl.value = urlByLabel;
			}

			isPlayingVideo.value = true;
			videoPlayerDialog.value?.open({
				callback: async () => {
					closeVideoPlayer();
					return true;
				},
			});
		} catch (error) {
			console.error(error);
			downloadErrorMessage.value = t("common.errorGettingLink", {
				key: s3key,
			});
		}
	}

	async function handleOpenFiles(s3key: string) {
		const extension = s3key.substring(s3key.lastIndexOf(".")).toLowerCase();
		const videoExtensions = [".mp4", ".mov", ".webm"];
		if (videoExtensions.includes(extension) && extension) {
			playVideoDocument(s3key);
		} else {
			downloadHelpFiles(s3key);
		}
	}
	async function closeVideoPlayer() {
		isPlayingVideo.value = false;
		selectedVideoUrl.value = null;
		selectedVideoTitle.value = null;
		selectedVideoKey.value = null;
		playlistItems.value = [];
	}

	async function handlePlaylistItemChange(item: PlaylistItem) {
		// Update the current video when user clicks a playlist item
		selectedVideoKey.value = item.key;
		selectedVideoTitle.value = item.title;
		selectedVideoUrl.value = item.videoUrl;
	}
</script>

<template>
	<div
		:class="[
			'grid',
			isAdding || isEditing
				? 'grid-cols-[18rem_1fr] pl-12'
				: 'grid grid-cols-[1fr]',
		]"
	>
		<ListFiles
			:files="displayFiles"
			:bucket-name="s3ResourceDocumentBucket"
			:loading="isLoading"
			:message="message"
			:managerFiles="false"
			:showTitle="true"
			:title="t('common.resourceDocuments')"
			:awsConfiguration="awsConfig"
			@openFile="handleOpenFiles"
		/>
		<AsyncDialog
			ref="videoPlayerDialog"
			v-slot="slotProps"
		>
			<VideoPlayer
				:videoUrl="selectedVideoUrl ?? ''"
				:videoTitle="selectedVideoTitle ?? t('common.video')"
				:playlistItems="playlistItems"
				:currentKey="selectedVideoKey ?? ''"
				@close="handleVideoPlayerClose(slotProps.close)"
				@playlistItemChange="handlePlaylistItemChange"
			/>
		</AsyncDialog>
	</div>
</template>

<style scoped>
	/* Line clamp utility for file names */
	.line-clamp-2 {
		display: -webkit-box;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;
	}
</style>
